import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { BACKEND_URL } from '../api/axios';
import Navbar from '../pages/Navbar';
import ApplicationTimeline from './ApplicationTimeline';

// const BACKEND_URL = 'http://127.0.0.1:8000';

const statusColors = {
  applied: 'bg-purple-100 text-[#9333EA]',
  viewed: 'bg-secondary-orange text-primary-orange',
  shortlisted: 'bg-secondary-green text-primary-green',
  interview: 'bg-secondary-orange text-primary-orange',
  selected: 'bg-secondary-green text-primary-green',
  screening: 'bg-secondary-orange text-primary-orange',
  contacted: 'bg-secondary-orange text-primary-orange',
  interview_round_1: 'bg-secondary-orange text-primary-orange',
  interview_round_2: 'bg-secondary-orange text-primary-orange',
  hr_round: 'bg-secondary-orange text-primary-orange',
  offer: 'bg-secondary-green text-primary-green',
  offer_accepted: 'bg-secondary-green text-primary-green',
  joined: 'bg-secondary-green text-primary-green',
  completed: 'bg-secondary-green text-primary-green',
  on_hold: 'bg-gray-100 text-gray-700',
  withdrawn: 'bg-gray-100 text-gray-500',
  rejected: 'bg-red-100 text-red-700',
};

const resolveLogoSrc = (logo) => {
  if (!logo) return null;
  return logo.startsWith('http') ? logo : `${BACKEND_URL}/storage/${logo}`;
};

function formatSalary(job) {
  if (!job) return null;
  if (job.salary_min && job.salary_max) {
    return `₹${Number(job.salary_min).toLocaleString('en-IN')} - ₹${Number(job.salary_max).toLocaleString('en-IN')}`;
  }
  if (job.salary) return job.salary;
  return null;
}

function MyApplications() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [timelineOpenId, setTimelineOpenId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [aggApplications, setAggApplications] = useState([]);
  const navigate = useNavigate();

  const PAGE_SIZE = 10;

  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const aggStatusColors = {
    pending_call: 'bg-secondary-orange text-primary-orange',
    confirmed: 'bg-secondary-green text-primary-green',
    rejected: 'bg-red-100 text-red-700',
    invalid_number: 'bg-gray-100 text-gray-500',
  };

  const combinedApplications = [
    ...applications.map((a) => ({ ...a, kind: 'internal' })),
    ...aggApplications.map((a) => ({ ...a, kind: 'aggregated' })),
  ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const fetchApplications = async () => {
    try {
      const [internalRes, aggRes] = await Promise.all([
        api.get('/my-applications'),
        api.get('/my-aggregated-applications'),
      ]);
      setApplications(internalRes.data);
      setAggApplications(aggRes.data);
    } catch (err) {
      console.error('Failed to fetch applications', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchApplications();
  }, [navigate]);

  const [withdrawingId, setWithdrawingId] = useState(null);

  const [withdrawTargetId, setWithdrawTargetId] = useState(null);
const [withdrawReason, setWithdrawReason] = useState('Personal reasons');
const [otherReason, setOtherReason] = useState('');
  // Applications in these statuses are terminal or already exited the
  // pipeline -- withdrawing them again is meaningless or already true.
  const WITHDRAW_BLOCKED_STATUSES = ['withdrawn', 'joined', 'completed', 'rejected'];

  const WITHDRAW_REASONS = [
  'Accepted another offer',
  'Personal reasons',
  'Salary dissatisfaction',
  'Location issues',
  'Role mismatch',
  'Unable to join',
  'Family commitments',
  'Further studies',
  'Other',
];

const openWithdrawModal = (id) => {
  setWithdrawTargetId(id);
  setWithdrawReason('Personal reasons');
  setOtherReason('');
};

const closeWithdrawModal = () => {
  if (withdrawingId) return;
  setWithdrawTargetId(null);
};

const handleWithdraw = async () => {
  const id = withdrawTargetId;
  const reason = withdrawReason === 'Other' ? otherReason.trim() : withdrawReason;

  if (!reason) {
    alert('Please type your reason.');
    return;
  }

  setWithdrawingId(id);
  try {
    await api.delete(`/applications/${id}`, { data: { reason } });
    setWithdrawTargetId(null);
    await fetchApplications();
  } catch (err) {
    console.error('Failed to withdraw application', err);
    alert('Could not withdraw the application. Please try again.');
  } finally {
    setWithdrawingId(null);
  }
};

  const toggleExpand = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const totalPages = Math.max(1, Math.ceil(combinedApplications.length / PAGE_SIZE));
  const pagedApplications = combinedApplications.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const goToPage = (page) => {
    const clamped = Math.min(Math.max(page, 1), totalPages);
    setCurrentPage(clamped);
    setExpandedId(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5FF] to-[#FDF2F8]">
      <Navbar user={user} />

      <div className="max-w-4xl mx-auto px-4 py-10">
        <h2 className="text-3xl font-bold mb-6 bg-gradient-to-r from-[#7C3AED] via-[#C026D3] to-[#EC4899] bg-clip-text text-transparent inline-block">My Applications</h2>

        {loading ? (
          <p className="text-[#8B7D9B]">Loading...</p>
        ) : combinedApplications.length === 0 ? (
          <div className="bg-white p-8 rounded-lg shadow text-center">
            <p className="text-[#8B7D9B] mb-4">You haven't applied to any jobs yet.</p>
            <Link to="/jobs" className="text-[#9333EA] font-medium hover:underline">
              Browse Jobs →
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {pagedApplications.map((app) => {
              // ---- Aggregated job application card ----
              if (app.kind === 'aggregated') {
                const job = app.job;
                const isExpanded = expandedId === `agg-${app.id}`;
                const salary = formatSalary(job);
                const logoSrc = resolveLogoSrc(job?.company_logo);

                return (
                  <div
                    key={`agg-${app.id}`}
                    className="bg-white rounded-xl shadow hover:shadow-md transition-shadow overflow-hidden flex flex-col"
                  >
                    <div
                      className={`h-1 ${
                        aggStatusColors[app.status]?.split(' ')[0] || 'bg-gray-200'
                      }`}
                    />

                    <div className="p-5 flex flex-col flex-1">
                      <div className="flex justify-between items-start gap-3">
                        <div className="flex items-start gap-3 min-w-0">
                          {logoSrc ? (
                            <img
                              src={logoSrc}
                              alt={job?.company_name}
                              className="w-11 h-11 rounded-lg object-cover shrink-0 border border-gray-100"
                            />
                          ) : (
                            <div className="w-11 h-11 rounded-lg bg-purple-50 text-[#9333EA] font-bold flex items-center justify-center shrink-0 text-lg">
                              {job?.company_name?.charAt(0)?.toUpperCase() || 'J'}
                            </div>
                          )}
                          <div className="min-w-0">
                            <h3 className="font-semibold text-[#1E1033] leading-snug truncate">
                              {job?.title}
                            </h3>
                            <p className="text-sm text-[#6B5B7B] truncate">{job?.company_name}</p>
                          </div>
                        </div>

                        <span
                          className={`shrink-0 text-xs font-medium px-3 py-1 rounded-full capitalize ${
                            aggStatusColors[app.status] || 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {app.status?.replace(/_/g, ' ')}
                        </span>
                      </div>

                      <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3 text-xs text-[#8B7D9B]">
                        {job?.location && <span>📍 {job.location}</span>}
                        {job?.job_type && (
                          <span className="capitalize">💼 {job.job_type.replace(/_/g, ' ')}</span>
                        )}
                        {salary && <span>💰 {salary}</span>}
                      </div>

                      <span className="inline-block w-fit mt-3 text-[10px] font-medium px-2.5 py-1 rounded-full bg-purple-50 text-[#8B7D9B]">
                        Aggregated — pending employer confirmation by our team
                      </span>

                      <div className="flex justify-between items-center mt-auto pt-4 border-t mt-4">
                        <p className="text-xs text-[#A99BB8]">
                          Applied {new Date(app.created_at).toLocaleDateString()}
                        </p>
                        {job?.id && (
                          <Link
                            to={`/aggregated-jobs/${job.id}`}
                            className="bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white text-xs font-semibold px-3 py-1.5 rounded-full hover:opacity-90 transition-opacity"
                          >
                            View Job →
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }

              // ---- Internal job application card (unchanged) ----
              const job = app.job_posting;
              const isExpanded = expandedId === app.id;
              const salary = formatSalary(job);
              const logoSrc = resolveLogoSrc(job?.company_logo || job?.employer?.company_logo);

              return (
                <div
                  key={app.id}
                  className="bg-white rounded-xl shadow hover:shadow-md transition-shadow overflow-hidden flex flex-col"
                >
                  {/* Top accent bar reflects status */}
                  <div
                    className={`h-1 ${
                      statusColors[app.status]?.split(' ')[0] || 'bg-gray-200'
                    }`}
                  />

                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        {/* Company logo / initial avatar */}
                        {logoSrc ? (
                          <img
                            src={logoSrc}
                            alt={job?.company_name}
                            className="w-11 h-11 rounded-lg object-cover shrink-0 border border-gray-100"
                          />
                        ) : (
                          <div className="w-11 h-11 rounded-lg bg-purple-50 text-[#9333EA] font-bold flex items-center justify-center shrink-0 text-lg">
                            {job?.company_name?.charAt(0)?.toUpperCase() || 'J'}
                          </div>
                        )}
                        <div className="min-w-0">
                          <h3 className="font-semibold text-[#1E1033] leading-snug truncate">
                            {job?.title}
                          </h3>
                          <p className="text-sm text-[#6B5B7B] truncate">{job?.company_name}</p>
                        </div>
                      </div>

                      <span
                        className={`shrink-0 text-xs font-medium px-3 py-1 rounded-full capitalize ${
                          statusColors[app.status] || 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        {app.status}
                      </span>
                    </div>

                    {/* Quick facts row — only shows fields that exist */}
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-3 text-xs text-[#8B7D9B]">
                      {job?.location && <span>📍 {job.location}</span>}
                      {job?.job_type && <span className="capitalize">💼 {job.job_type}</span>}
                      {job?.experience_required && <span>🧭 {job.experience_required}</span>}
                      {salary && <span>💰 {salary}</span>}
                      {job?.work_mode && <span className="capitalize">🏠 {job.work_mode}</span>}
                    </div>

                    {/* Skills required */}
                    {Array.isArray(job?.skills_required) && job.skills_required.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {job.skills_required.slice(0, 5).map((skill, i) => (
                          <span
                            key={i}
                            className="bg-purple-50 text-[#9333EA] text-xs font-medium px-2 py-0.5 rounded-full"
                          >
                            {skill}
                          </span>
                        ))}
                        {job.skills_required.length > 5 && (
                          <span className="text-xs text-[#A99BB8] self-center">
                            +{job.skills_required.length - 5} more
                          </span>
                        )}
                      </div>
                    )}

                    {/* Expandable description */}
                    {job?.description && (
                      <div className="mt-3">
                        <p className={`text-sm text-[#6B5B7B] ${isExpanded ? '' : 'line-clamp-2'}`}>
                          {job.description}
                        </p>
                        <button
                          onClick={() => toggleExpand(app.id)}
                          className="text-[#9333EA] text-xs font-medium mt-1 hover:underline"
                        >
                          {isExpanded ? 'Show less' : 'Read full description'}
                        </button>
                      </div>
                    )}

                    <div className="mt-3">
                      <button
                        onClick={() => setTimelineOpenId((p) => (p === app.id ? null : app.id))}
                        className="text-[#9333EA] text-xs font-medium hover:underline"
                      >
                        {timelineOpenId === app.id ? 'Hide timeline' : 'View timeline'}
                      </button>
                      {timelineOpenId === app.id && <ApplicationTimeline applicationId={app.id} />}
                    </div>

                    <div className="flex justify-between items-center mt-auto pt-4 border-t mt-4">
                      <p className="text-xs text-[#A99BB8]">
                        Applied {new Date(app.created_at).toLocaleDateString()}
                      </p>
                      <div className="flex items-center gap-2">
                        {app.offer?.response_token && ['sent', 'viewed', 'negotiation'].includes(app.offer.status) && (
                          <Link
                            to={`/offers/respond/${app.offer.response_token}`}
                            className="bg-emerald-500 text-white text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-emerald-600 transition-colors"
                          >
                            View Offer →
                          </Link>
                        )}
                        {job?.id && (
                          <Link
                            to={`/jobs/${job.id}`}
                            className="bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white text-xs font-semibold px-3 py-1.5 rounded-full hover:opacity-90 transition-opacity"
                          >
                            View Job →
                          </Link>
                        )}
                        {app.kind === 'internal' && !WITHDRAW_BLOCKED_STATUSES.includes(app.status) && (
                         <button
  onClick={() => openWithdrawModal(app.id)}
  disabled={withdrawingId === app.id}
  className="bg-red-50 text-red-600 text-xs font-semibold px-3 py-1.5 rounded-full hover:bg-red-100 disabled:opacity-50 transition-colors"
>
  {withdrawingId === app.id ? 'Withdrawing...' : 'Withdraw'}
</button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!loading && combinedApplications.length > PAGE_SIZE && (
          <div className="flex items-center justify-center gap-1.5 mt-8">
            <button
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1.5 text-sm font-medium rounded-lg border text-[#1E1033] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-purple-50"
            >
              ← Prev
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => goToPage(page)}
                className={`w-9 h-9 text-sm font-medium rounded-lg border ${
                  page === currentPage
                    ? 'bg-[#9333EA] text-white border-[#9333EA]'
                    : 'text-[#1E1033] hover:bg-purple-50'
                }`}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1.5 text-sm font-medium rounded-lg border text-[#1E1033] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-purple-50"
            >
              Next →
            </button>
          </div>
        )}

        {withdrawTargetId && (
  <div
    className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
    onClick={closeWithdrawModal}
  >
    <div
      className="bg-white rounded-xl shadow-xl w-full max-w-md p-6"
      onClick={(e) => e.stopPropagation()}
    >
      <h3 className="text-lg font-bold text-[#1E1033]">Withdraw application</h3>
      <p className="text-sm text-[#6B5B7B] mt-1">
        Tell us why you are withdrawing. You can re-apply to the same job later.
      </p>

      <label className="block text-sm font-medium text-[#1E1033] mt-4 mb-1">Reason</label>
      <select
        value={withdrawReason}
        onChange={(e) => setWithdrawReason(e.target.value)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#9333EA]"
      >
        {WITHDRAW_REASONS.map((r) => (
          <option key={r} value={r}>{r}</option>
        ))}
      </select>

      {withdrawReason === 'Other' && (
        <textarea
          value={otherReason}
          onChange={(e) => setOtherReason(e.target.value)}
          rows={3}
          maxLength={500}
          placeholder="Type your reason..."
          className="w-full mt-3 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#9333EA]"
        />
      )}

      <div className="flex justify-end gap-2 mt-5">
        <button
          onClick={closeWithdrawModal}
          disabled={!!withdrawingId}
          className="px-4 py-2 text-sm font-medium rounded-lg border text-[#1E1033] hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={handleWithdraw}
          disabled={!!withdrawingId}
          className="px-4 py-2 text-sm font-semibold rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
        >
          {withdrawingId ? 'Withdrawing...' : 'Withdraw'}
        </button>
      </div>
    </div>
  </div>
)}
      </div>
    </div>
  );
}

export default MyApplications;